import {collection, getFirestore} from 'firebase/firestore';
import {serverTimestamp} from 'firebase/firestore';
import {addDoc} from 'firebase/firestore';
import {useCourseId} from 'hooks/router/useUrlParams';
import {getUserId, useUserId} from 'hooks/user/useUserId';
import {useCallback} from 'react';
import type {UserId} from 'types/common';
import type {CourseId} from 'types/common';
import {BUILD_COMMIT, IS_DEVELOPMENT, toFirebaseSafeObject} from 'utils/general';
import {getOrCreateSessionId} from '@cip/core/utils';
import {getStackTrace} from './getStackTrace';

/** Longest summary kept for metadata that cannot be stored as-is. */
const MAX_METADATA_SUMMARY_CHARS = 4000;

/**
 * A string summary of any value for a log entry. Functions, symbols and
 * bigints are named, errors keep their name, message and stack, and a
 * circular reference is marked rather than followed. Capped at
 * `MAX_METADATA_SUMMARY_CHARS` characters.
 *
 * @param value - The value to describe.
 * @return The summary.
 * @throws Never.
 */
export function summarizeForLog(value: unknown): string {
  const seen = new WeakSet<object>();
  let summary: string;
  try {
    summary =
      JSON.stringify(value, (_key, item: unknown) => {
        if (typeof item === 'function') {
          return `[function ${item.name || 'anonymous'}]`;
        }
        if (typeof item === 'symbol' || typeof item === 'bigint') {
          return `[${typeof item} ${String(item)}]`;
        }
        if (item instanceof Error) {
          return {name: item.name, message: item.message, stack: item.stack};
        }
        if (typeof item === 'object' && item !== null) {
          if (seen.has(item)) {
            return '[circular]';
          }
          seen.add(item);
        }
        return item;
      }) ?? String(value);
  } catch (error) {
    console.debug('[errors] metadata could not be summarized as JSON', error);
    summary = String(value);
  }
  return summary.slice(0, MAX_METADATA_SUMMARY_CHARS);
}

/**
 * Log metadata in a form Firestore stores. When some value cannot be stored,
 * the metadata is kept as a summary with the reason, so the log entry is
 * still written: a logger that throws loses the entry it was writing.
 *
 * @param metadata - Caller metadata. Omitted means none.
 * @return Storable fields to spread into the log document.
 * @throws Never.
 */
export function toStorableLogMetadata(
  metadata: Record<string, unknown> | undefined,
): Record<string, unknown> {
  if (metadata === undefined) {
    return {};
  }
  try {
    return toFirebaseSafeObject(metadata);
  } catch (error) {
    return {
      metadataSummary: summarizeForLog(metadata),
      metadataError: error instanceof Error ? error.message : String(error),
    };
  }
}

interface ProductionErrorLoggerResult {
  /**
   * Reports an error in the production environment to firebase.
   *
   * Attaches metadata about the user and site when the error occurred. In
   * development, this function does nothing.
   *
   * @param error - The error to report.
   * @param additionalMetadata - (Optional) Arbitrary metadata to attach to the
   * error.
   */
  logProductionErrorToFirestore: (
    error: Error,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
    additionalMetadata?: Record<string, any>,
  ) => Promise<void>;
}
export function useProductionErrorLogger(): ProductionErrorLoggerResult {
  const userId = useUserId();
  const courseId = useCourseId();

  if (IS_DEVELOPMENT) {
    return {
      logProductionErrorToFirestore: async (
         
        // eslint-disable-next-line @typescript-eslint/no-unused-vars -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
        error: Error,
         
        // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unused-vars -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
        additionalMetadata?: Record<string, any>,
      ) => {},
    };
  }

  // eslint-disable-next-line react-hooks/rules-of-hooks -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  const logProductionErrorToFirestore = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
    async (error: Error, additionalMetadata: Record<string, any> = {}) => {
      await logProductionErrorNoHook({
        error,
        courseId,
        userId,
        additionalMetadata,
      });
    },
    [userId, courseId],
  );

  return {
    logProductionErrorToFirestore,
  };
}

interface LogProductionErrorNoHookParams {
  error: Error;
  courseId?: CourseId;
  userId?: UserId;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  additionalMetadata?: Record<string, any>;
}
export async function logProductionErrorNoHook(
  params: LogProductionErrorNoHookParams,
) {
  const {error, courseId, userId, additionalMetadata} = params;
  const db = getFirestore();
  const stackTrace = getStackTrace(error);
  // Fall back to the Firebase auth singleton when the caller didn't supply a
  // userId, so log entries are always attributable when a user is signed in.
  const resolvedUserId = userId ?? getUserId();
  await addDoc(collection(db, 'errorLogs'), {
    url: window.location.href,
    errorMessage: error.message,
    errorStack: stackTrace,
    userId: resolvedUserId ?? null,
    courseId: courseId ?? null,
    commit: BUILD_COMMIT,
    sessionId: getOrCreateSessionId(),
    createdAt: serverTimestamp(),
    additionalMetadata: toStorableLogMetadata(additionalMetadata),
  });
}

interface LogProductionMessageToFirestoreProps {
  /**
   * The message to log.
   */
  message: string;
  /**
   * Whether to log to the console (only relevant in development).
   */
  alsoLogToConsole?: boolean;
  /**
   * The collection to log to. Defaults to 'errorLogs' (same as
   * logProductionErrorToFirestore).
   */
  logCollection?: string;
  /**
   * Additional metadata to log.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  [additionalField: string]: any;
}

/**
 * Logs a message to the production environment to firebase.
 *
 * Attaches metadata about the user and site when the message occurred. In
 * development, this function does nothing unless alsoLogToConsole is true.
 *
 * @param message - The message to log.
 * @param alsoLogToConsole - Whether to log to the console.
 */
export const logProductionMessageToFirestore = async ({
  message,
  alsoLogToConsole,
  logCollection,
  ...additionalMetadata
}: LogProductionMessageToFirestoreProps) => {
  if (IS_DEVELOPMENT) {
    if (alsoLogToConsole) {
      if (additionalMetadata) {
        console.log(
          message,
          Object.entries(additionalMetadata)
            .map(([key, value]) => `${key}: ${value}`)
            .join(', '),
        );
      } else {
        console.log(message);
      }
    }
    return;
  }

  const db = getFirestore();
  const collectionName = logCollection ?? 'errorLogs';
  // Caller-supplied `userId` in additionalMetadata wins; otherwise read the
  // auth singleton so log entries are always attributable when a user is
  // signed in.
  const fallbackUserId = getUserId();
  await addDoc(collection(db, collectionName), {
    url: window.location.href,
    logMessage: message,
    userId: fallbackUserId ?? null,
    ...toStorableLogMetadata(additionalMetadata),
    commit: BUILD_COMMIT,
    sessionId: getOrCreateSessionId(),
    // Kept out of toStorableLogMetadata, which would mangle the sentinel.
    createdAt: serverTimestamp(),
  });
};

export {getStackTrace};
